/**
 * Collector-map marker helpers — clustering, copy, and camera padding.
 *
 * Pure functions on purpose. The map used to compute all of this inline in
 * MapPage's render, which meant the two things most worth testing — whether a
 * cluster is stable across renders, and whether a copy line is actually TRUE —
 * could only be reached through a Mapbox mock. Both live here instead.
 *
 * Two rules hold throughout:
 *   1. No dependency. There is no supercluster in package.json and clustering
 *      does not justify adding one; a deterministic geographic grid is enough
 *      for the marker counts this map renders.
 *   2. Never state anything the payload cannot support. The nearby-collector
 *      projection is `name image publicLocation locationPrivacy lastLogin
 *      verification reputation` — there is no neighbourhood, city, or "last
 *      seen" in it. Where a design line wants a fact we do not have, the
 *      segment is dropped rather than guessed.
 */

/** Collectors within this many screen-independent grid cells collapse into one cluster. */
const CLUSTER_CELL_SPAN = 1;

/** Non-finite input is not a coordinate, whatever its type says. */
const finite = (v) => typeof v === "number" && Number.isFinite(v);

/**
 * Read a collector's position out of either supported shape.
 *
 * The map normalizers hand over `coordinates: [lng, lat]`, while the geolocation
 * path and some callers build `{ longitude, latitude }`. Both are read here so
 * no caller has to normalize before clustering.
 */
function readLngLat(collector) {
  if (!collector || typeof collector !== "object") return null;
  const raw = collector.coordinates;

  if (Array.isArray(raw) && raw.length >= 2) {
    const [lng, lat] = raw;
    if (finite(lng) && finite(lat)) return { longitude: lng, latitude: lat };
    return null;
  }

  if (raw && typeof raw === "object") {
    const { longitude, latitude } = raw;
    if (finite(longitude) && finite(latitude)) return { longitude, latitude };
    return null;
  }

  // Last resort: the position sitting directly on the collector.
  if (finite(collector.longitude) && finite(collector.latitude)) {
    return { longitude: collector.longitude, latitude: collector.latitude };
  }
  return null;
}

/**
 * Group nearby collectors into clusters so a dense area reads as one target.
 *
 * Deterministic by construction: the cell a collector lands in is a pure
 * function of its coordinates and the zoom, cluster ids are derived from the
 * cell indices (never from array position or a counter), and buckets are
 * emitted in sorted key order so two calls with the same input produce the same
 * array in the same order. That matters because a cluster whose id or order
 * shifts between renders makes React tear down and rebuild every marker.
 *
 * @param {Array<object>} collectors
 * @param {{ zoom?: number, minClusterSize?: number }} [options]
 * @returns {{ singles: Array<object>, clusters: Array<{ id: string, longitude: number, latitude: number, count: number, members: Array<object> }> }}
 */
export function clusterCollectors(collectors, options = {}) {
  const { zoom = 12, minClusterSize = 2 } = options || {};
  const list = Array.isArray(collectors) ? collectors : [];

  // Cell size shrinks as zoom grows, so clusters dissolve on the way in rather
  // than needing a separate "stop clustering above zoom N" rule.
  const z = Math.max(0, Math.min(22, Math.round(Number(zoom) || 0)));
  const cell = (360 / Math.pow(2, z + 3)) * CLUSTER_CELL_SPAN;

  const buckets = new Map();
  list.forEach((collector) => {
    const coords = readLngLat(collector);
    if (!coords) return; // No publishable position: nothing to place on the map.
    const cx = Math.floor(coords.longitude / cell);
    const cy = Math.floor(coords.latitude / cell);
    const key = `${cx}:${cy}`;
    const bucket = buckets.get(key);
    if (bucket) {
      bucket.members.push(collector);
      bucket.coords.push(coords);
    } else {
      buckets.set(key, { cx, cy, members: [collector], coords: [coords] });
    }
  });

  const rawThreshold = Number(minClusterSize);
  const threshold = Number.isFinite(rawThreshold) ? Math.max(1, rawThreshold) : 2;
  const singles = [];
  const clusters = [];

  // Sorted keys, not insertion order — the output must not depend on how the
  // input array happened to be ordered.
  const keys = [...buckets.keys()].sort();

  /**
   * MERGE ACROSS CELL WALLS.
   *
   * The grid above is a spatial INDEX, not the answer. Using the cell as the
   * answer meant a hidden boundary decided whether two markers clustered: two
   * collectors a few metres apart, drawn on top of each other, stayed separate
   * pins purely because `Math.floor` put them either side of a wall they could
   * not see. That is the defect this pass exists to remove — a cell edge is an
   * implementation detail and must never be visible in the product.
   *
   * Each bucket is unioned with any of its 8 neighbours whose centroid is
   * within one cell, so proximity decides membership and the grid only decides
   * who is worth comparing. That keeps the whole thing O(n) rather than the
   * O(n²) an all-pairs sweep would cost on a dense map.
   *
   * Why not Supercluster: `clusterCollectors` guarantees an id derived purely
   * from geometry, so the same collectors produce the same cluster id whatever
   * order the array arrived in (pinned by mapMarkers.test.js). Supercluster's
   * ids encode a point's INDEX in the input, so a reordered poll response would
   * rename every cluster and make React tear down and rebuild every marker on
   * the map. The boundary defect is real; that cure is worse.
   */
  const parent = {};
  keys.forEach((k) => {
    parent[k] = k;
  });
  const find = (k) => {
    let root = k;
    while (parent[root] !== root) root = parent[root];
    // Path compression, so a long chain does not re-walk on every lookup.
    let walk = k;
    while (parent[walk] !== root) {
      const next = parent[walk];
      parent[walk] = root;
      walk = next;
    }
    return root;
  };
  const union = (a, b) => {
    const ra = find(a);
    const rb = find(b);
    if (ra === rb) return;
    // Smaller key wins so the shape of the tree cannot depend on visit order.
    if (ra < rb) parent[rb] = ra;
    else parent[ra] = rb;
  };

  const centroidOf = (bucket) => {
    const sum = bucket.coords.reduce(
      (acc, c) => ({ lng: acc.lng + c.longitude, lat: acc.lat + c.latitude }),
      { lng: 0, lat: 0 }
    );
    return { lng: sum.lng / bucket.coords.length, lat: sum.lat / bucket.coords.length };
  };

  const centroids = {};
  keys.forEach((key) => {
    centroids[key] = centroidOf(buckets.get(key));
  });

  keys.forEach((key) => {
    const bucket = buckets.get(key);
    const here = centroids[key];
    for (let dx = -1; dx <= 1; dx += 1) {
      for (let dy = -1; dy <= 1; dy += 1) {
        if (dx === 0 && dy === 0) continue;
        const neighbourKey = `${bucket.cx + dx}:${bucket.cy + dy}`;
        if (!buckets.has(neighbourKey)) continue;
        const there = centroids[neighbourKey];
        // Chebyshev distance in degrees against the cell size: "close enough
        // that one bubble should speak for both", the same span the cell itself
        // encodes.
        if (Math.abs(here.lng - there.lng) <= cell && Math.abs(here.lat - there.lat) <= cell) {
          union(key, neighbourKey);
        }
      }
    }
  });

  // Group by root, in sorted key order throughout so the emitted array and
  // every cluster's id are pure functions of geometry.
  const groups = {};
  keys.forEach((key) => {
    const root = find(key);
    if (!groups[root]) groups[root] = [];
    groups[root].push(key);
  });

  Object.keys(groups)
    .sort()
    .forEach((root) => {
      const memberKeys = groups[root].sort();
      const members = [];
      const coords = [];
      memberKeys.forEach((key) => {
        const bucket = buckets.get(key);
        bucket.members.forEach((m) => members.push(m));
        bucket.coords.forEach((c) => coords.push(c));
      });

      // Stable member order, by identity rather than by however the poll
      // happened to return them. The bubble draws the first two members as
      // faces, so an unstable order meant the same unchanged cluster showed
      // different people between refreshes — a flicker with no event behind it.
      members.sort((a, b) => {
        const ida = String(a?.id ?? a?._id ?? "");
        const idb = String(b?.id ?? b?._id ?? "");
        return ida < idb ? -1 : ida > idb ? 1 : 0;
      });

      if (members.length < threshold) {
        members.forEach((m) => singles.push(m));
        return;
      }

      const sum = coords.reduce(
        (acc, c) => ({ lng: acc.lng + c.longitude, lat: acc.lat + c.latitude }),
        { lng: 0, lat: 0 }
      );
      clusters.push({
        // Derived from the group's lowest cell, not from a counter and not from
        // whichever cell happened to be visited first.
        id: `cluster:${memberKeys[0]}`,
        longitude: sum.lng / coords.length,
        latitude: sum.lat / coords.length,
        count: members.length,
        members,
      });
    });

  return { singles, clusters };
}

/** Screen-reader label for a cluster bubble. */
export function clusterAccessibleLabel(count) {
  const n = Math.max(0, Math.round(Number(count) || 0));
  return `${n} collector${n === 1 ? "" : "s"} in this area`;
}

/**
 * First word of whatever name we have — a map marker is not the place for a
 * full legal name, and the preview it opens shows the rest anyway.
 */
function displayFirstName(collector) {
  const candidates = [collector?.username, collector?.name?.first];
  for (const candidate of candidates) {
    if (typeof candidate !== "string") continue;
    const first = candidate.trim().split(/\s+/)[0];
    if (first) return first;
  }
  return "this collector";
}

/** Screen-reader label for a single collector marker. */
export function collectorAccessibleLabel(collector) {
  return `Open ${displayFirstName(collector)}'s collector preview`;
}

/**
 * "Active now", or nothing.
 *
 * The payload carries `lastLogin`, which the map has already reduced to a
 * boolean online flag. That boolean is all we can honestly say: there is no
 * session history behind it, so a softer fallback like "active earlier today"
 * would be invented. Returning null lets the caller drop the segment.
 */
export function activityCopy(collector) {
  if (!collector) return null;
  const online = collector.online === true || collector.onlineStatus === "Online";
  return online ? "Active now" : null;
}

/**
 * The meta line under a collector's name: `Near {area} · Active now`.
 *
 * The area segment is OMITTED whenever `area` is absent, and that omission is
 * deliberate. The server never sends a neighbourhood or city for a nearby
 * collector — only a jittered `[lng, lat]` — so there is nothing to render but
 * a guess. Reverse-geocoding the jittered point would name a place the person
 * may not be in, and a distance in metres is worse still: it is precise enough
 * to help triangulate a home address, which is exactly what the jitter exists
 * to prevent. An area name is the only location detail that is both useful and
 * privacy-safe, so it is the only one shown, and only when we have it.
 */
export function collectorMetaLine(collector) {
  const segments = [];
  const area = collector?.area;
  if (typeof area === "string" && area.trim()) segments.push(`Near ${area.trim()}`);
  const activity = activityCopy(collector);
  if (activity) segments.push(activity);
  return segments.join(" · ");
}

/**
 * Trust in one line. Reads the flattened `successfulTrades` the map payloads
 * carry, falling back to the nested `reputation` shape for callers holding a
 * raw user document.
 */
export function trustCopy(collector) {
  const raw =
    typeof collector?.successfulTrades === "number"
      ? collector.successfulTrades
      : collector?.reputation?.successfulTrades;
  const n = Number(raw) || 0;
  if (n > 0) return `${n} trade${n === 1 ? "" : "s"} completed`;
  return "New to Tradeloft";
}

/** Ids arrive as ObjectIds, strings, or populated docs; compare them as strings. */
const idStr = (v) => {
  if (v == null) return "";
  if (typeof v === "object") return v._id != null ? String(v._id) : "";
  return String(v);
};

/**
 * How many OTHER collectors are nearby.
 *
 * The signed-in user is injected into the marker list as their own pin, so the
 * raw list length counted the viewer — "14 collectors nearby" meant thirteen
 * other people and you.
 */
export function nearbyCollectorCount(collectors, currentUserId) {
  if (!Array.isArray(collectors)) return 0;
  const me = idStr(currentUserId);
  if (!me) return collectors.length;
  return collectors.filter((c) => idStr(c?.id) !== me && idStr(c?._id) !== me).length;
}

/**
 * Camera padding: the single place the map learns what chrome covers it.
 *
 * Every fit/recenter reads its insets from here, so an element that overlaps
 * the map is accounted for once instead of being re-guessed per call site. The
 * open preview sheet adds its own height to the bottom inset — otherwise a
 * "fit these collectors" move centres them behind the sheet.
 */
export function viewportPaddingFor(options = {}) {
  const {
    top = 0,
    bottom = 0,
    left = 0,
    right = 0,
    sheetOpen = false,
    sheetPx = 0,
  } = options || {};
  // Negative or non-finite padding is never meaningful to a camera.
  const px = (v) => {
    const n = Number(v);
    return Number.isFinite(n) && n > 0 ? n : 0;
  };
  return {
    top: px(top),
    bottom: px(bottom) + (sheetOpen ? px(sheetPx) : 0),
    left: px(left),
    right: px(right),
  };
}
